import { gql } from '@apollo/client';
import { Box, Checkbox, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { t } from 'i18next';
import { TableInstance } from 'react-table';
import UpgradeToWaveEssentialEmptyState from '~components/organization/UpgradeToWaveEssentialModal/EmptyState';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { SubscriptionsTableV2SubscriptionFragment } from './__generated__/SubscriptionsTable.graphql';
import { SubscriptionsTableV2PageSizeSelector, SubscriptionsTableV2Pagination } from './Pagination';

interface Props {
  table: TableInstance<SubscriptionsTableV2SubscriptionFragment>;
}

export function SubscriptionsTableV2({ table }: Props) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;

  return (
    <Table {...getTableProps()} variant="simple" width="100%">
      <Thead>
        {headerGroups.map((headerGroup) => {
          const headerGroupProps = headerGroup.getHeaderGroupProps();

          return (
            <Tr {...headerGroupProps} key={headerGroupProps.key}>
              {headerGroup.headers.map((column) => {
                const sortProps = column.getSortByToggleProps();
                const sortColumn = sortProps.onClick;
                const headerProps = column.getHeaderProps(sortProps);

                return (
                  <Th
                    {...headerProps}
                    key={headerProps.key}
                    color={column.isSorted ? 'gray.700' : 'gray.600'}
                    minWidth={`${column.minWidth}px`}
                    maxWidth={`${column.maxWidth}px`}
                    width={column.width}
                    _hover={
                      column.disableSortBy
                        ? {}
                        : {
                            textColor: 'gray.800',
                          }
                    }
                    onClick={sortColumn}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <Box flex="1">{column.render('Header')}</Box>
                      {column.canSort && (
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <ChevronDownIcon display="inherit" />
                            ) : (
                              <ChevronUpIcon display="inherit" />
                            )
                          ) : (
                            <ChevronDownIcon visibility="hidden" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Th>
                );
              })}
            </Tr>
          );
        })}
      </Thead>
      <Tbody {...getTableBodyProps()}>
        {page.map((row) => {
          prepareRow(row);
          const rowProps = row.getRowProps();

          return (
            <Tr {...rowProps} key={rowProps.key}>
              {row.cells.map((cell) => {
                const cellProps = cell.getCellProps();
                return (
                  <Td
                    {...cellProps}
                    key={cellProps.key}
                    minWidth={`${cell.column.minWidth}px`}
                    maxWidth={`${cell.column.maxWidth}px`}
                    width={cell.column.width}
                  >
                    {cell.render('Cell')}
                  </Td>
                );
              })}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
}

export function SubscriptionsTableV2HideExpired({
  isChecked,
  hideExpired,
  onChange,
  onHideExpiredChange,
}: {
  isChecked?: boolean;
  hideExpired?: boolean;
  onChange?: (checked: boolean) => void;
  onHideExpiredChange?: (checked: boolean) => void;
}) {
  const checked = isChecked ?? hideExpired ?? false;
  const handleChange = (val: boolean) => {
    onChange?.(val);
    onHideExpiredChange?.(val);
  };

  return (
    <Box mb="4">
      <Checkbox isChecked={checked} onChange={(e) => handleChange(e.target.checked)}>
        {t('subscriptionHideExpired')}
      </Checkbox>
    </Box>
  );
}

SubscriptionsTableV2.EmptyView = UpgradeToWaveEssentialEmptyState;
SubscriptionsTableV2.Pagination = SubscriptionsTableV2Pagination;
SubscriptionsTableV2.PageSizeSelector = SubscriptionsTableV2PageSizeSelector;
SubscriptionsTableV2.HideExpired = SubscriptionsTableV2HideExpired;

SubscriptionsTableV2.graphql = {
  fragments: {
    SubscriptionsTableV2Subscription: gql`
      fragment SubscriptionsTableV2Subscription on WaveSubscription {
        id
        name
        userGivenName
        customers {
          name
        }
        createdAt
        validUntil
        usage {
          max
          current
        }
        status
        productId
      }
    `,
  },
};
